import React from 'react'
import { Modal, Button, Input, Label, Badge } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import type { Customer } from '../types'
import { updateCustomerName, adminChangeCustomerPlan } from '../services/customerService'
import { listPlans } from '@/modules/plans/services/planService'
import type { Plan } from '@/modules/plans/types'
import { getOwnerContactsByAccountIds } from '@/modules/accounts/services/ownerContactService'
import { buildWhatsAppLink } from '@/shared/utils/whatsapp'
import { formatPhoneBR } from '@/modules/profile/utils/phone'

type Props = {
  open: boolean
  onOpenChange: (v: boolean) => void
  customer: Customer | null
  onSaved?: () => void
}

export default function CustomerEditModal({ open, onOpenChange, customer, onSaved }: Props) {
  const { add: toast } = useToast()
  const [name, setName] = React.useState(customer?.name ?? '')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [plans, setPlans] = React.useState<Plan[]>([])
  const [selectedPlanId, setSelectedPlanId] = React.useState<string | null>(customer?.current_plan_id ?? null)
  const [ownerPhone, setOwnerPhone] = React.useState<string | null>(null)
  const [ownerCountry, setOwnerCountry] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) return
    setName(customer?.name ?? '')
    setSelectedPlanId(customer?.current_plan_id ?? null)
    setSaving(false)
    setError(null)
    ;(async () => {
      try {
        const { items } = await listPlans({ page: 1, pageSize: 200 })
        const active = items.filter(p => p.active)
        const exists = active.some(p => p.id === (customer?.current_plan_id || ''))
        setPlans(exists || !customer?.current_plan_id ? active : [...active, ...(items.filter(p => p.id === customer?.current_plan_id))])
      } catch {}
    })()
    ;(async () => {
      try {
        if (!customer?.id) { setOwnerPhone(null); setOwnerCountry(null); return }
        const map = await getOwnerContactsByAccountIds([customer.id])
        const oc = map.get(customer.id) as any
        setOwnerPhone(oc?.phone ?? null)
        setOwnerCountry((oc?.country || null) as string | null)
      } catch {}
    })()
  }, [open, customer?.id])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!customer) return
    setError(null)
    try {
      setSaving(true)
      const newName = (name || '').trim()
      if (newName && newName !== (customer.name || '')) {
        await updateCustomerName(customer.id, newName)
      }
      const targetPlan = (selectedPlanId || '').trim()
      if (targetPlan && targetPlan !== (customer.current_plan_id || '')) {
        const res = await adminChangeCustomerPlan(customer.id, targetPlan)
        if (res.mode === 'already_on_plan') {
          toast({ title: 'Plano já ativo', description: 'Nenhuma alteração foi aplicada porque o cliente já está no plano lógico selecionado.', variant: 'info', soft: true })
        } else if (res.mode === 'upgrade') {
          toast({ title: 'Plano atualizado', description: 'Upgrade aplicado imediatamente.', variant: 'success', soft: true })
        } else {
          const when = res.scheduled_for ? new Date(res.scheduled_for).toISOString().replace('T',' ').slice(0,19) + ' UTC' : 'proximo ciclo'
          toast({ title: 'Downgrade agendado', description: `Valido a partir de ${when}.`, variant: 'info', soft: true })
        }
      }
      onSaved?.()
      onOpenChange(false)
    } catch (err: any) {
      setError(err?.message ?? 'Falha ao salvar alteracoes do cliente')
    } finally {
      setSaving(false)
    }
  }

  const createdAt = customer?.created_at
    ? new Date(customer.created_at).toISOString().replace('T',' ').slice(0,19) + ' UTC'
    : '-'

  const phoneDigits = (ownerPhone || '').toString().replace(/\D/g, '')
  const isBR = phoneDigits.startsWith('55') || (ownerCountry || '').toUpperCase() === 'BR'
  const displayPhone = phoneDigits
    ? (isBR ? formatPhoneBR(phoneDigits.replace(/^55/, '')) : phoneDigits.replace(/(\d{3})(?=\d)/g, '$1 ').trim())
    : '-'
  const waUrl = ownerPhone ? buildWhatsAppLink(ownerPhone, 'Ola! Aqui e do suporte do Cripto Alert.') : '#'

  return (
    <Modal open={open} onOpenChange={onOpenChange} className="p-6 sm:p-8" style={{ width: 680, maxWidth: 680 }}>
      <div className="mb-4 text-xl font-semibold">Editar cliente</div>
      <form className="grid gap-4 sm:grid-cols-2" onSubmit={onSubmit}>
        {error && (
          <div className="sm:col-span-2 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
            {error}
          </div>
        )}

        <div className="space-y-1.5 sm:col-span-2">
          <Label>Nome da conta</Label>
          <Input value={name} onChange={(e)=> setName(e.target.value)} placeholder="Nome do cliente/conta" />
        </div>

        <div className="space-y-1.5">
          <Label>Email do titular</Label>
          <Input value={customer?.email || '-'} readOnly disabled />
        </div>

        <div className="space-y-1.5">
          <Label>Status</Label>
          <div className="h-12 rounded-md border border-input bg-background px-3 text-sm flex items-center">
            {customer?.status ? (
              <Badge
                variant="soft"
                color={
                  customer.status === 'ativo' ? 'success' :
                  customer.status === 'pendente' ? 'info' :
                  customer.status === 'suspenso' ? 'warning' :
                  'error'
                }
                className="capitalize"
              >
                {customer.status}
              </Badge>
            ) : '-'}
          </div>
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          <Label>Telefone (WhatsApp)</Label>
          <div className="flex items-center gap-2">
            <Input value={displayPhone} readOnly disabled className="flex-1" />
            <Button asChild disabled={!ownerPhone}>
              <a href={waUrl} target="_blank" rel="noopener noreferrer">WhatsApp</a>
            </Button>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Plano atual</Label>
          <select
            className="h-12 w-full rounded-md border border-input bg-background px-3 text-sm"
            value={selectedPlanId ?? ''}
            onChange={(e)=> setSelectedPlanId(e.target.value || null)}
          >
            <option value="">Sem alteracao ({customer?.current_plan_name || '-'})</option>
            {plans.map((p)=> (
              <option key={p.id} value={p.id}>{p.name} • {(p.amount_cents/100).toLocaleString('pt-BR',{ style:'currency', currency: p.currency || 'BRL' })} / {p.interval === 'month' ? 'mes' : (p.interval === 'year' ? 'ano' : p.interval)}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <Label>Data de cadastro</Label>
          <Input value={createdAt} readOnly disabled />
        </div>

        <div className="sm:col-span-2 mt-2 flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={()=> onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Salvando...' : 'Salvar alteracoes'}</Button>
        </div>
      </form>
    </Modal>
  )
}
